// Mirrors ssgp-v3-platform/ssgp-v3-contracts/ApiEnvelope.cs. Every /api/v3/*
// endpoint returns ApiResponse<T>; the FE unwraps with `unwrap<T>()` (throws
// on failure). Field shapes are load-bearing — drift here breaks every hook.

import axios, { AxiosResponse } from 'axios';

export interface ApiMeta {
  requestId: string;
  /** ISO 8601 timestamp. */
  timestamp: string;
  processingTimeMs?: number;
}

export interface ApiError {
  /** Stable machine-readable code — see ApiErrorCodes. */
  code: string;
  /** Human-readable, safe-for-client message. */
  message: string;
  /** Optional structured detail — e.g. field-level validation failures. */
  details?: Record<string, unknown>;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error: ApiError | null;
  meta: ApiMeta;
}

/** Canonical error codes returned by the v3 envelope. */
export const ApiErrorCodes = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  CONFLICT: 'CONFLICT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  /** Studio-typed metadata failed schema validation (Path C Slice 6) — e.g. a
   *  value not in the field's `allowedValues`, or an unknown field when the
   *  schema sets `rejectUnknownFields`. Maps to 400; surface the field-level
   *  `details` to the user. */
  STUDIO_METADATA_INVALID: 'STUDIO_METADATA_INVALID',
  /** Transient infra failure (DB unreachable, EF retry limit, upstream provider
   *  timeout). Clients SHOULD retry with backoff; the request itself is not
   *  malformed. TanStack Query's default retry policy already covers 5xx — no
   *  special handling needed beyond toast-message wording. */
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  /** The endpoint is in the contract but not implemented yet (501) — batch
   *  coordination. Distinguishes "not built" from "broke"; not retryable. */
  NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
  /** No provider key of their own for the chosen model, and not enough credit
   *  to run it on ours (402). `details` carries `{ requiredNgn, balanceNgn,
   *  shortfallNgn, currency }`. Never surface this as a bare error: the caller
   *  has two real ways forward — top up, or add their own key in settings —
   *  so it should open the top-up flow, not a red alert. See `useCredits`. */
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
} as const;

export type ApiErrorCode = (typeof ApiErrorCodes)[keyof typeof ApiErrorCodes];

/**
 * Thrown by `unwrap` (and the axios response interceptor) when the envelope
 * carries `success: false`. Preserves the error code, details, and request id
 * so callers can branch on `code` without parsing the message.
 */
export class ApiResponseError extends Error {
  readonly code: string;
  readonly details?: Record<string, unknown>;
  readonly requestId?: string;
  /** HTTP status — populated when thrown from the axios interceptor. */
  readonly status?: number;

  constructor(error: ApiError, meta?: ApiMeta | null, status?: number) {
    super(error.message || 'API call failed');
    this.name = 'ApiResponseError';
    this.code = error.code;
    this.details = error.details;
    this.requestId = meta?.requestId;
    this.status = status;
  }
}

/**
 * Throws on `success: false`; otherwise returns `data`. The `T` parameter is
 * what the caller declares — void-returning endpoints are typed `unwrap<null>`
 * or just `unwrap<void>` and the caller accepts that null is the success value.
 */
export function unwrap<T>(response: ApiResponse<T>, status?: number): T {
  if (response.success) {
    return response.data as T;
  }
  const error: ApiError = response.error ?? {
    code: ApiErrorCodes.INTERNAL_ERROR,
    message: 'Malformed API response: success was false but no error provided.',
  };
  throw new ApiResponseError(error, response.meta, status);
}

export function isApiResponse<T = unknown>(value: unknown): value is ApiResponse<T> {
  return (
    value !== null &&
    typeof value === 'object' &&
    'success' in value &&
    typeof (value as ApiResponse<T>).success === 'boolean' &&
    'meta' in value
  );
}

/**
 * Service-layer adapter: takes an axios call returning ApiResponse<T>, unwraps
 * on success. On envelope-shaped 4xx failures (axios rejects them by default),
 * translates the error body into ApiResponseError. Non-envelope failures
 * (network drop, 401 from the interceptor, 5xx without a body) propagate
 * unchanged so callers can keep axios's existing error semantics.
 */
export async function callV3<T>(
  call: Promise<AxiosResponse<ApiResponse<T>>>,
): Promise<T> {
  try {
    const response = await call;
    return unwrap(response.data, response.status);
  } catch (e) {
    if (axios.isAxiosError(e) && isApiResponse<T>(e.response?.data)) {
      const env = e.response!.data as ApiResponse<T>;
      const error: ApiError = env.error ?? {
        code: ApiErrorCodes.INTERNAL_ERROR,
        message: e.message || 'API call failed',
      };
      throw new ApiResponseError(error, env.meta, e.response?.status);
    }
    throw e;
  }
}
